import { useEffect, useState } from "react";
import FormSections from "../../../../../components/sections/admin/Form/FormSections";
import { FileText } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { dynamicFieldAddEdit, dynamicFormAddEdit, dynamicSectionAddEdit } from "../../../../../services/dynamicFormService";
import { toast } from "react-toastify";

const FormDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const form = location?.state?.form;

  const [formDetails, setFormDetails] = useState({
    title: "Untitled Form",
    description: "form description...",
    sectionDetail: [
      {
        _id:null,
        tempId: crypto.randomUUID(),
        title: "Untitled Section",
        description: "section description...",
        relatedField: [
          {
            _id:null,
            tempId: crypto.randomUUID(),
            label: { text: "Untitled Field", image: null },
            type: "input",
            placeholder: "New field",
            options: [],
          },
        ],
        required: false,
      },
    ],
  });

  useEffect(() => {
    if (form) setFormDetails(form);
  }, [form]);

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async ({ formPayload, sectionPayload }) => {
      const formRes = await dynamicFormAddEdit(formPayload);

      const sectionRes = [];
      for (const sec of sectionPayload) {
        if (sec.id) {
        const res = await dynamicSectionAddEdit(sec);
        sectionRes.push(res);
        }
      }

      const fieldRes = [];
      for (const fld of fieldPayload) {
        if (fld.id) {
        const res = await dynamicFieldAddEdit(fld);
        fieldRes.push(res);
        }
      }

      return { formRes, sectionRes, fieldRes };
    },
    onSuccess: () => {
      toast.success("Form saved successfully");
      navigate("/admin/forms");
    },
    onError: (err) => console.error("Error saving:", err),
  });

  const formPayload = {
  ...(formDetails._id && { id: formDetails._id }),
  title: formDetails.title,
  description: formDetails.description,
  section: formDetails.sectionDetail.map((section) => ({
    ...(section._id && { id: section._id }),
    title: section.title,
    description: section.description,
    key: section?.key,
    field: section.relatedField.map((field) => ({
      ...(field._id && { id: field._id }),
      label: { text: field.label?.text || "" },
      type: field.type,
      placeholder: field.placeholder,
      options:
        field.options?.map((opt) => ({
          ...(opt._id && { _id: opt._id }),
          default: opt.default || false,
          label: opt.label || "",
        })) || [],
    })),
  })),
};

  const sectionPayload = formDetails?.sectionDetail.map((section) => ({
    id: section._id,
    title: section.title,
    description: section.description,
    field:section?.relatedField.map((field) => ({
      label: {text:field?.label?.text || ""} ,
      ...(field?.key && {key:field?.key}),
      type: field?.type,
      placeholder: field.placeholder,
      options:
        field.options?.map((opt) => ({
          default: opt.default || false,
          key: opt?.key,
          label: opt.label,
        })) || [],
    }))
  }));

  const fieldPayload = formDetails?.sectionDetail.flatMap((section) =>
    section?.relatedField.map((field) => ({
      id: field._id,
      labelText: field?.label?.text,
      type: field?.type,
      placeholder: field.placeholder,
      options:
        field.options?.map((opt) => ({
          default: opt.default || false,
          key: opt?.key,
          label: opt.label,
        })) || [],
    }))
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    mutateAsync({
      formPayload,
      sectionPayload,
      fieldPayload,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-transparent">
      <div className="w-full bg-white border-b shadow-sm px-6 py-3 grid grid-cols-2 items-center gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <FileText className="text-blue-600 w-10 h-10" />
            <input
              type="text"
              value={formDetails.title}
              onChange={(e) => setFormDetails((prev) => ({ ...prev, title: e.target.value }))}
              className="text-2xl font-bold text-gray-800 outline-none bg-transparent border-b border-transparent focus:border-indigo-500 transition flex-1"
              placeholder="Enter form title..."
            />
          </div>

          <textarea
            value={formDetails.description}
            onChange={(e) =>
              setFormDetails((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            className="text-gray-600 text-sm ml-[53px] outline-none bg-transparent border-b border-transparent focus:border-indigo-400 transition resize-none w-full max-w-[500px] h-40px]"
            placeholder="Enter form description..."
            rows={1}
          />
        </div>

        <div className="fixed right-[50px] flex justify-end items-start z-70">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium shadow hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? "Saving..." : "Save Form"}
          </button>
        </div>
      </div>

      <FormSections formDetails={formDetails} setFormDetails={setFormDetails} />
    </form>
  );
};

export default FormDetails;
